/**
 * Follow-up Tagging System
 *
 * Provides utilities for managing follow-up tags on deals.
 * Tags are week-aware and automatically reset each week.
 *
 * Tag Format: "followed-up-{YYYY-W##}" (e.g., "followed-up-2026-W15")
 * This allows tracking follow-ups across weeks and identifying which deals
 * have been followed up on in the current week.
 *
 * The ISO-week computation lives in `./iso-week` and is shared with the snooze
 * tagging system (`./snooze-tagging`) so both resolve identical week strings.
 */

import { getISOWeekString } from "./iso-week";

// Re-exported for backward compatibility — existing callers import it from here.
export { getISOWeekString };

/**
 * Get the follow-up tag for the current week
 * Example: "followed-up-2026-W15"
 */
export function getCurrentWeekFollowUpTag(): string {
  return `followed-up-${getISOWeekString()}`;
}

/**
 * Check if a deal has been followed up on this week
 */
export function hasBeenFollowedUpThisWeek(tags?: string[]): boolean {
  if (!tags || tags.length === 0) return false;
  const currentWeekTag = getCurrentWeekFollowUpTag();
  return tags.includes(currentWeekTag);
}

/**
 * Add the current week's follow-up tag to a deal's tags
 * Returns the updated tags array
 */
export function addFollowUpTagThisWeek(tags?: string[]): string[] {
  const currentTags = tags || [];
  const currentWeekTag = getCurrentWeekFollowUpTag();
  
  if (currentTags.includes(currentWeekTag)) {
    return currentTags;
  }
  
  return [...currentTags, currentWeekTag];
}

/**
 * Remove the current week's follow-up tag from a deal's tags
 * Returns the updated tags array
 */
export function removeFollowUpTagThisWeek(tags?: string[]): string[] {
  if (!tags || tags.length === 0) return [];
  const currentWeekTag = getCurrentWeekFollowUpTag();
  return tags.filter(tag => tag !== currentWeekTag);
}

/**
 * Toggle the current week's follow-up tag
 * Returns the updated tags array
 */
export function toggleFollowUpTagThisWeek(tags?: string[]): string[] {
  if (hasBeenFollowedUpThisWeek(tags)) {
    return removeFollowUpTagThisWeek(tags);
  }
  return addFollowUpTagThisWeek(tags);
}

/**
 * Get all follow-up tags (across all weeks) from a tags array
 * Useful for seeing historical follow-up activity
 */
export function getFollowUpTags(tags?: string[]): string[] {
  if (!tags || tags.length === 0) return [];
  return tags.filter(tag => tag.startsWith("followed-up-"));
}

/**
 * Get non-follow-up tags (all other tags)
 * Useful for filtering out follow-up tags when displaying other metadata
 */
export function getNonFollowUpTags(tags?: string[]): string[] {
  if (!tags || tags.length === 0) return [];
  return tags.filter(tag => !tag.startsWith("followed-up-"));
}

/**
 * Check if a deal needs follow-up (hasn't been followed up this week)
 */
export function needsFollowUp(tags?: string[]): boolean {
  return !hasBeenFollowedUpThisWeek(tags);
}
